import './MobilePagination.scss';

import {
    CloseOutlined,
    DoubleLeftOutlined,
    DoubleRightOutlined,
    DownOutlined,
    LeftOutlined,
    RightOutlined,
} from '@ant-design/icons';
import React, {useEffect, useId, useRef, useState} from 'react';

import * as localizationKeys from '../../../consts/localization/localizationKeys';
import {getLocalizedStrings} from '../../../services/localization/localizationService';

const MobilePagination = ({paginationOptions = {}, currentPage, rowCount, totalRecords}) => {
    const localizedStrings = getLocalizedStrings();
    const jumpId = useId();
    const jumpRef = useRef(null);
    const [isJumpOpen, setIsJumpOpen] = useState(false);
    const [draftPage, setDraftPage] = useState('');
    const [hasPageError, setHasPageError] = useState(false);
    const hasPaginationData =
        Number.isInteger(currentPage) &&
        currentPage > 0 &&
        Number.isInteger(rowCount) &&
        rowCount > 0 &&
        Number.isInteger(totalRecords) &&
        totalRecords >= 0;
    const totalPages = hasPaginationData ? Math.max(Math.ceil(totalRecords / rowCount), 1) : 1;
    const activePage = hasPaginationData ? Math.min(currentPage, totalPages) : 1;
    const {onChange, onShowSizeChange, disabled, showSizeChanger, hideOnSinglePage} = paginationOptions;

    useEffect(() => {
        setIsJumpOpen(false);
        setHasPageError(false);
    }, [activePage, rowCount, totalPages]);

    useEffect(() => {
        if (isJumpOpen) jumpRef.current?.scrollIntoView({block: 'nearest'});
    }, [isJumpOpen]);

    if (!hasPaginationData || (hideOnSinglePage && totalPages === 1)) return null;

    const rangeStart = totalRecords ? (activePage - 1) * rowCount + 1 : 0;
    const rangeEnd = Math.min(activePage * rowCount, totalRecords);
    const pageSizeOptions = [...new Set([...(paginationOptions.pageSizeOptions || [10, 20, 50]), rowCount].map(Number))]
        .filter((size) => Number.isSafeInteger(size) && size > 0)
        .sort((left, right) => left - right);
    const changePage = (page) => {
        if (disabled) return;

        const nextPage = Math.max(1, Math.min(page, totalPages));

        if (nextPage !== activePage) onChange?.(nextPage, rowCount);
        setIsJumpOpen(false);
    };
    const submitPage = (event) => {
        event.preventDefault();

        const pageText = draftPage.trim();
        const page = Number(pageText);

        if (!/^\d+$/.test(pageText) || !Number.isSafeInteger(page) || page < 1 || page > totalPages) {
            setHasPageError(true);
            return;
        }

        changePage(page);
    };
    const pageLabel = localizedStrings.formatString(
        localizedStrings[localizationKeys.MOBILE_PAGINATION_CURRENT],
        activePage,
        totalPages
    );

    return (
        <nav
            className='mobile-table-pagination'
            aria-label={localizedStrings[localizationKeys.MOBILE_PAGINATION_LABEL]}
        >
            {isJumpOpen && (
                <form ref={jumpRef} id={jumpId} className='mobile-pagination-jump' onSubmit={submitPage} noValidate>
                    <div className='mobile-pagination-jump-heading'>
                        <label htmlFor={`${jumpId}-input`}>
                            {localizedStrings[localizationKeys.MOBILE_PAGINATION_JUMP]}
                        </label>
                        <button
                            type='button'
                            className='mobile-pagination-jump-close'
                            aria-label={localizedStrings[localizationKeys.MOBILE_PAGINATION_CLOSE]}
                            onClick={() => setIsJumpOpen(false)}
                        >
                            <CloseOutlined aria-hidden />
                        </button>
                    </div>
                    <div className='mobile-pagination-jump-controls'>
                        <input
                            id={`${jumpId}-input`}
                            name='page'
                            type='text'
                            inputMode='numeric'
                            enterKeyHint='go'
                            autoComplete='off'
                            value={draftPage}
                            disabled={disabled}
                            aria-invalid={hasPageError}
                            aria-describedby={`${jumpId}-hint`}
                            onChange={(event) => {
                                setDraftPage(event.target.value);
                                setHasPageError(false);
                            }}
                        />
                        <button type='submit' className='mobile-pagination-go' disabled={disabled}>
                            {localizedStrings[localizationKeys.MOBILE_PAGINATION_GO]}
                            <RightOutlined aria-hidden />
                        </button>
                    </div>
                    <p id={`${jumpId}-hint`} className={hasPageError ? 'is-error' : undefined} aria-live='polite'>
                        {localizedStrings.formatString(
                            localizedStrings[
                                hasPageError
                                    ? localizationKeys.MOBILE_PAGINATION_INVALID
                                    : localizationKeys.MOBILE_PAGINATION_RANGE
                            ],
                            totalPages
                        )}
                    </p>
                </form>
            )}
            <div className='mobile-pagination-summary'>
                <span className='mobile-pagination-range' role='status'>
                    {localizedStrings.formatString(
                        localizedStrings[localizationKeys.MOBILE_PAGINATION_RECORDS],
                        rangeStart,
                        rangeEnd,
                        totalRecords
                    )}
                </span>
                {showSizeChanger !== false && (
                    <label className='mobile-pagination-size'>
                        <span className='sr-only'>{localizedStrings[localizationKeys.MOBILE_PAGINATION_SIZE]}</span>
                        <select
                            name='pageSize'
                            value={rowCount}
                            disabled={disabled}
                            onChange={(event) => {
                                const size = Number(event.target.value);

                                onShowSizeChange?.(1, size);
                                onChange?.(1, size);
                                setIsJumpOpen(false);
                            }}
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {localizedStrings.formatString(
                                        localizedStrings[localizationKeys.MOBILE_PAGINATION_PER_PAGE],
                                        size
                                    )}
                                </option>
                            ))}
                        </select>
                        <DownOutlined aria-hidden />
                    </label>
                )}
            </div>
            <div className='mobile-pagination-navigation'>
                <button
                    type='button'
                    aria-label={localizedStrings[localizationKeys.MOBILE_PAGINATION_FIRST]}
                    disabled={disabled || activePage === 1}
                    onClick={() => changePage(1)}
                >
                    <DoubleLeftOutlined aria-hidden />
                </button>
                <button
                    type='button'
                    aria-label={localizedStrings[localizationKeys.MOBILE_PAGINATION_PREVIOUS]}
                    disabled={disabled || activePage === 1}
                    onClick={() => changePage(activePage - 1)}
                >
                    <LeftOutlined aria-hidden />
                </button>
                <button
                    type='button'
                    className='mobile-pagination-current'
                    aria-label={`${pageLabel}. ${localizedStrings[localizationKeys.MOBILE_PAGINATION_JUMP]}`}
                    aria-expanded={isJumpOpen}
                    aria-controls={jumpId}
                    disabled={disabled || totalPages === 1}
                    onClick={() => {
                        setDraftPage(String(activePage));
                        setHasPageError(false);
                        setIsJumpOpen((open) => !open);
                    }}
                >
                    <span>
                        {activePage} <span>/ {totalPages}</span>
                    </span>
                    {totalPages > 1 && <DownOutlined aria-hidden />}
                </button>
                <button
                    type='button'
                    aria-label={localizedStrings[localizationKeys.MOBILE_PAGINATION_NEXT]}
                    disabled={disabled || activePage === totalPages}
                    onClick={() => changePage(activePage + 1)}
                >
                    <RightOutlined aria-hidden />
                </button>
                <button
                    type='button'
                    aria-label={localizedStrings[localizationKeys.MOBILE_PAGINATION_LAST]}
                    disabled={disabled || activePage === totalPages}
                    onClick={() => changePage(totalPages)}
                >
                    <DoubleRightOutlined aria-hidden />
                </button>
            </div>
        </nav>
    );
};

export default MobilePagination;
